import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PushNotificationCenterPopup from '../components/PushNotificationCenterPopup';
import {
  appendNotificationHistory,
  appendNotificationHistoryIfNew,
  getNotificationHistory,
  NOTIFICATION_HISTORY_CHANGED,
} from '../utils/notificationHistory';
import { SITE_BRANDING_UPDATED_EVENT } from '../utils/siteBranding';
import { isAdminPanelPath } from '../constants/adminPanelRoutes';
import { notificationService } from '../services/api';
import {
  clearUnreadNotificationCount,
  getUnreadNotificationCount,
  incrementUnreadNotificationCount,
  isCoarsePointerMobile,
  resetFaviconBadgeCache,
  syncNotificationIndicators,
  vibrateNotificationPulse,
} from '../utils/notificationIndicators';

const PREFS_KEY = 'sheen_notification_prefs';

const defaultPrefs = {
  emailOrderUpdates: true,
  emailPromotional: true,
  emailNewsletter: false,
  pushOrderAlerts: true,
  pushPriceDrops: false,
};

function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) return { ...defaultPrefs };
    const parsed = JSON.parse(raw);
    return { ...defaultPrefs, ...parsed };
  } catch (e) {
    return { ...defaultPrefs };
  }
}

function savePrefs(prefs) {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch (e) {}
}

const PushNotificationContext = createContext(null);

export function PushNotificationProvider({ children }) {
  const [prefs, setPrefs] = useState(loadPrefs);
  const [centerPopup, setCenterPopup] = useState(null);
  const [notifUiRevision, setNotifUiRevision] = useState(0);

  const recentNotifications = useMemo(() => {
    const list = getNotificationHistory(25);
    const unread = getUnreadNotificationCount();
    if (unread > 0 && list.length === 0) {
      return [
        {
          id: '__synthetic_unread__',
          title: 'Unread notifications',
          message:
            unread === 1
              ? 'You have an unread notification. Tap to open notification settings and activity.'
              : `You have ${unread} unread notifications. Tap to review.`,
          path: '/profile',
          profileSection: 'notifications',
          kind: 'synthetic',
          createdAt: Date.now(),
        },
      ];
    }
    return list;
  }, [notifUiRevision]);

  const unreadCount = useMemo(
    () => getUnreadNotificationCount(),
    [notifUiRevision]
  );

  useEffect(() => {
    const bump = () => setNotifUiRevision((x) => x + 1);
    window.addEventListener(NOTIFICATION_HISTORY_CHANGED, bump);
    return () => window.removeEventListener(NOTIFICATION_HISTORY_CHANGED, bump);
  }, []);

  const clearUnread = useCallback(() => {
    clearUnreadNotificationCount();
    syncNotificationIndicators(0);
    setNotifUiRevision((x) => x + 1);
  }, []);

  const updatePrefs = useCallback((partial) => {
    setPrefs((prev) => {
      const next = { ...prev, ...partial };
      savePrefs(next);
      return next;
    });
  }, []);

  const deliverPushMessage = useCallback(
    (message, options = {}) => {
      const {
        forceVibrate = true,
        path = '/profile',
        profileSection = 'notifications',
        kind = 'push',
        title,
      } = options;
      if (!message) return;
      const stored = appendNotificationHistory({
        message,
        title: title || (kind === 'order' ? 'Order update' : 'Alert'),
        path,
        profileSection: path === '/profile' ? profileSection : null,
        kind,
      });
      if (stored) {
        const total = incrementUnreadNotificationCount();
        syncNotificationIndicators(total);
      }
      if (forceVibrate && isCoarsePointerMobile()) {
        vibrateNotificationPulse();
      }
      setCenterPopup({ message, id: Date.now() });
    },
    []
  );

  useEffect(() => {
    if (!centerPopup) return undefined;
    const t = window.setTimeout(() => setCenterPopup(null), 5000);
    return () => window.clearTimeout(t);
  }, [centerPopup]);

  useEffect(() => {
    syncNotificationIndicators(getUnreadNotificationCount());
  }, []);

  useEffect(() => {
    const onBranding = () => {
      resetFaviconBadgeCache();
      syncNotificationIndicators(getUnreadNotificationCount());
    };
    window.addEventListener(SITE_BRANDING_UPDATED_EVENT, onBranding);
    return () => window.removeEventListener(SITE_BRANDING_UPDATED_EVENT, onBranding);
  }, []);

  /** Pull server notifications for logged-in shoppers; order-related ones appear in the bell. */
  useEffect(() => {
    let cancelled = false;
    const syncOrderNotificationsFromApi = async () => {
      if (cancelled || typeof window === 'undefined') return;
      if (isAdminPanelPath(window.location.pathname)) return;
      const token = localStorage.getItem('authToken');
      if (!token) return;

      try {
        const res = await notificationService.getNotifications();
        const raw = res.notifications ?? res.data;
        let items = [];
        if (Array.isArray(raw)) items = raw;
        else if (raw && typeof raw === 'object' && Array.isArray(raw.notifications)) {
          items = raw.notifications;
        } else if (raw && typeof raw === 'object' && Array.isArray(raw.data)) {
          items = raw.data;
        }

        let added = false;
        for (const n of items) {
          if (!n || n.read === true || n.isRead === true) continue;
          const ext = n._id ?? n.id ?? n.notificationId;
          if (ext == null || ext === '') continue;
          const title = String(n.title ?? n.subject ?? 'Order update');
          const message = String(n.message ?? n.body ?? n.text ?? '').trim();
          const typeStr = String(n.type ?? n.category ?? '').toLowerCase();
          const hay = `${title} ${message} ${typeStr}`.toLowerCase();
          if (!hay.includes('order') && !typeStr.includes('order')) continue;
          const stored = appendNotificationHistoryIfNew({
            title,
            message: message || 'Your order was updated.',
            path: '/orders',
            profileSection: null,
            kind: 'order',
            externalId: String(ext),
          });
          if (stored) {
            added = true;
            incrementUnreadNotificationCount();
          }
        }
        if (added) {
          syncNotificationIndicators(getUnreadNotificationCount());
          setNotifUiRevision((x) => x + 1);
        }
      } catch {
        /* offline or 401 */
      }
    };

    syncOrderNotificationsFromApi();
    const intervalId = window.setInterval(syncOrderNotificationsFromApi, 90000);
    window.addEventListener('focus', syncOrderNotificationsFromApi);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', syncOrderNotificationsFromApi);
    };
  }, []);

  useEffect(() => {
    const onNotify = (e) => {
      const prefsNow = loadPrefs();
      const kind = e.detail?.kind;
      if (kind === 'price' && !prefsNow.pushPriceDrops) return;
      if (kind === 'order' && !prefsNow.pushOrderAlerts) return;
      if (
        !kind &&
        !prefsNow.pushOrderAlerts &&
        !prefsNow.pushPriceDrops
      ) {
        return;
      }
      const message =
        e.detail?.message ||
        e.detail?.body ||
        'You have a new notification.';
      const path = e.detail?.path || (kind === 'price' ? '/profile' : '/profile');
      const profileSection =
        e.detail?.profileSection ||
        (kind === 'price' ? 'profile' : 'notifications');
      const title = e.detail?.title;
      deliverPushMessage(message, {
        kind: kind || 'push',
        path,
        profileSection,
        title,
      });
    };

    const onCheckoutSuccess = () => {
      const prefsNow = loadPrefs();
      if (!prefsNow.pushOrderAlerts) return;
      deliverPushMessage(
        'Order update: your order was placed successfully. We will keep you posted on its status.',
        {
          kind: 'order',
          path: '/orders',
          profileSection: null,
          title: 'Order placed',
        }
      );
    };

    window.addEventListener('sheenPushNotify', onNotify);
    window.addEventListener('showCheckoutSuccess', onCheckoutSuccess);
    return () => {
      window.removeEventListener('sheenPushNotify', onNotify);
      window.removeEventListener('showCheckoutSuccess', onCheckoutSuccess);
    };
  }, [deliverPushMessage]);

  const value = useMemo(
    () => ({
      prefs,
      updatePrefs,
      clearUnread,
      centerPopup,
      dismissCenterPopup: () => setCenterPopup(null),
      recentNotifications,
      unreadCount,
    }),
    [prefs, updatePrefs, clearUnread, centerPopup, recentNotifications, unreadCount]
  );

  return (
    <PushNotificationContext.Provider value={value}>
      {children}
      {centerPopup ? (
        <PushNotificationCenterPopup
          message={centerPopup.message}
          onDismiss={() => setCenterPopup(null)}
        />
      ) : null}
    </PushNotificationContext.Provider>
  );
}

export function usePushNotifications() {
  const ctx = useContext(PushNotificationContext);
  if (!ctx) {
    throw new Error('usePushNotifications must be used within PushNotificationProvider');
  }
  return ctx;
}

export { PREFS_KEY, defaultPrefs, loadPrefs };
